import { useRef } from 'react';

import { ScaleThreshold, scaleLinear, scaleThreshold } from 'd3-scale';
import { Color, PickingInfo } from 'deck.gl';
import Draggable from 'react-draggable';

import { ThemeProvider, rgbToHex, useTheme } from '@mui/material/styles';

import { IconButton } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import CloseIcon from '@mui/icons-material/Close';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import WhatshotIcon from '@mui/icons-material/Whatshot';

import { getAvatarByManufacturerName } from '@/utils';

import dayjs, { _dayjs } from '@/dayjs';

import { useFormatters } from '@/hooks';

import { ContainerInsightsTemperatureBreachesExpanded } from '../Heatmap';

// Gray color for N/A
const naRgbColor: Color = [110, 110, 110];

const colorRange: Color[] = [
  [1, 152, 189], // rgb(1, 152, 189)
  [73, 227, 206], // rgb(73, 227, 206)
  [216, 254, 181], // rgb(216, 254, 181)
  [254, 237, 177], //rgb(254,237,177)
  [254, 173, 84], //rgb(254,173,84)
  [209, 55, 78], //rgb(209,55,78)
];

interface MapTooltipProps {
  popup: PickingInfo;
  // colorScale: ScaleThreshold<number, Color>;
  elevation: number;
  onClose: () => void;
}

function MapTooltip(props: MapTooltipProps) {
  const { popup, /* colorScale,*/ elevation, onClose } = props;
  const theme = useTheme();

  if (!popup || !popup.object) {
    return null;
  }

  const { object } = popup;

  // console.log(popup);

  const temperatureBreaches: ContainerInsightsTemperatureBreachesExpanded[] =
    object.points
      .map((point: any) => point.source)
      .sort(
        (
          a: ContainerInsightsTemperatureBreachesExpanded,
          b: ContainerInsightsTemperatureBreachesExpanded,
        ) =>
          dayjs(a.breach_start_datetime).unix() -
          dayjs(b.breach_start_datetime).unix(),
      );

  const { formatNumber } = useFormatters();

  // const rgbColor = /* object.
  //   ? colorScale(object.)
  //   :*/ naRgbColor;
  const rgbColor = colorRange[object.colorValue] || naRgbColor;

  const hexColor = rgbToHex(
    `rgb(${rgbColor[0]}, ${rgbColor[1]}, ${rgbColor[2]})`,
  );

  const nodeRef = useRef<HTMLDivElement | null>(null);

  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-handle"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper
        ref={nodeRef}
        elevation={elevation}
        sx={{
          zIndex: 3,
          position: 'absolute',
          backgroundColor: 'white',
          left: popup.x,
          top: popup.y,
          m: 2,
          p: 2,
        }}
      >
        <Stack direction="column" spacing={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            flexGrow={1}
          >
            <DragHandleIcon id="draggable-handle" sx={{ cursor: 'move' }} />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent={'center'}
              flexGrow={1}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  icon={<WhatshotIcon />}
                  label={
                    temperatureBreaches.length > 1
                      ? `${temperatureBreaches.length} temperature breaches`
                      : `${temperatureBreaches.length} temperature breach`
                  }
                  color="primary"
                  sx={{ borderRadius: 1 }}
                />
              </Stack>
            </Stack>

            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Divider />

          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ maxHeight: 300, overflowY: 'auto' }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Reefer ID</TableCell>
                  <TableCell align="left">Model</TableCell>
                  <TableCell align="left">Started at</TableCell>
                  <TableCell align="left">Ended at</TableCell>
                  <TableCell align="left">Duration</TableCell>
                  <TableCell align="right">Max deviation</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {temperatureBreaches.map(
                  (
                    temperatureBreach: ContainerInsightsTemperatureBreachesExpanded,
                    index: number,
                  ) => {
                    const start = dayjs(
                      temperatureBreach.breach_start_datetime,
                    );
                    const end = dayjs(temperatureBreach.breach_end_datetime);

                    let diff = end.diff(start, 'days');
                    let duration = `${diff} day${diff > 1 ? 's' : ''}`;
                    if (!diff) {
                      diff = end.diff(start, 'hours');
                      duration = `${diff} hour${diff > 1 ? 's' : ''}`;
                    }

                    return (
                      <TableRow
                        key={index}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                      >
                        <TableCell component="th" scope="row">
                          <Typography variant="caption">
                            {temperatureBreach.reefer?.id}
                          </Typography>
                        </TableCell>

                        <TableCell align="left">
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="flex-start"
                            spacing={1}
                          >
                            <Avatar
                              variant="rounded"
                              slotProps={{
                                img: { sx: { objectFit: 'contain' } },
                              }}
                              src={getAvatarByManufacturerName(
                                temperatureBreach.reefer
                                  ?.manufacturer as string,
                              )}
                              sx={{
                                p: 0.5,
                                width: 36,
                                height: 36,
                              }}
                            >
                              {temperatureBreach.reefer?.manufacturer?.charAt(
                                0,
                              )}
                            </Avatar>
                            <Typography variant="caption">
                              {
                                temperatureBreach.reefer
                                  ?.refrigeration_unit_model
                              }
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell component="th" scope="row" align="left">
                          <Typography variant="caption">
                            {dayjs(
                              temperatureBreach.breach_start_datetime,
                            ).format('lll')}
                          </Typography>
                        </TableCell>
                        <TableCell component="th" scope="row" align="left">
                          <Typography variant="caption">
                            {dayjs(
                              temperatureBreach.breach_end_datetime,
                            ).format('lll')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption">{duration}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption">
                            {temperatureBreach.max_deviation.toFixed(2)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  },
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Paper>
    </Draggable>
  );
}

export { MapTooltip };
